/**
 * AI Edge Score + Live Trading Readiness — Performance Lab tab.
 *
 * Design rules (per spec):
 *  - When the Edge Score is not eligible, we show the checklist only, never a number.
 *  - Live Trading remains OFF at the app level; readiness is advisory.
 *  - Every gate shows required threshold + current value + pass/fail.
 */
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Loader2, ShieldAlert, RefreshCw, CheckCircle2, XCircle, HelpCircle, Info, Zap, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const LABEL_TONE = {
  "Strong":              "border-signal-green text-signal-green",
  "Validated":           "border-signal-green/70 text-signal-green",
  "Promising":           "border-[#0A84FF] text-[#0A84FF]",
  "Emerging":            "border-signal-yellow text-signal-yellow",
  "Unproven":            "border-signal-red/70 text-signal-red",
  "Collecting Evidence": "border-border/60 text-muted-foreground",
};

const READINESS_TONE = {
  "Not Ready":                  "border-signal-red text-signal-red",
  "Collecting Data":            "border-border/60 text-muted-foreground",
  "Limited Evidence":           "border-signal-yellow text-signal-yellow",
  "Ready for Small Live Trial": "border-[#0A84FF] text-[#0A84FF]",
  "Ready for Controlled Scaling": "border-signal-green text-signal-green",
};

const COMPONENT_LABELS = {
  benchmark_alpha:          "Benchmark Alpha",
  walk_forward_stability:   "Walk-Forward Stability",
  expectancy:               "Expectancy",
  profit_factor:            "Profit Factor",
  sharpe_sortino:           "Sharpe & Sortino",
  max_drawdown:             "Max Drawdown",
  benchmark_consistency:    "Benchmark Consistency",
  overfitting_risk:         "Overfitting Risk",
  monte_carlo:              "Monte Carlo",
  confidence_calibration:   "Confidence Calibration",
  cost_efficiency:          "Cost Efficiency",
  recommendation_stability: "Recommendation Stability",
  sample_maturity:          "Sample Maturity",
  regime_coverage:          "Regime Coverage",
};

const GATE_LABELS = {
  paper_days:               "Paper-Trading Duration",
  completed_trades:         "Completed Trades",
  positive_expectancy:      "Positive Expectancy",
  profit_factor:            "Profit Factor",
  sharpe:                   "Sharpe Ratio",
  max_drawdown:             "Max Drawdown",
  benchmark_alpha:          "Benchmark Alpha",
  monte_carlo_prob_profit:  "Monte Carlo Prob(profit)",
  ruin_prob:                "Ruin Probability",
  walk_forward_windows:     "Walk-Forward Windows",
  overfitting_risk:         "Overfitting Risk",
  calibration_brier:        "Calibration Brier",
  edge_score:               "AI Edge Score",
  regime_coverage:          "Regime Coverage",
  no_execution_failures:    "Execution Reliability",
  broker_reconciled:        "Broker Reconciliation",
};

function fmt(v, d = 2) {
  if (v == null) return "N/A";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "string") return v;
  if (!isFinite(v)) return "N/A";
  return v.toFixed(d);
}

function ScoreCard({ edge, onRecompute, recomputing }) {
  const eligible = !!edge?.eligible;
  const label = edge?.label || "Collecting Evidence";
  const score = edge?.score;
  return (
    <div className="grid-cell !p-4 space-y-3" data-testid="edge-score-card">
      <div className="flex items-center gap-3 flex-wrap">
        <Zap size={16} className="text-[#0A84FF]"/>
        <div className="mono uppercase text-[10px] tracking-widest text-muted-foreground">AI Edge Score</div>
        <div className={`chip !text-[10px] uppercase ${LABEL_TONE[label] || ""}`} data-testid="edge-label">{label}</div>
        <div className="ml-auto">
          <button className="btn-outline" onClick={onRecompute} disabled={recomputing} data-testid="edge-recompute">
            {recomputing ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>}
            Recompute
          </button>
        </div>
      </div>
      {eligible ? (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-border/60">
          <div className="p-3 bg-card/40 col-span-2">
            <div className="label-cap">Overall Score</div>
            <div className="mono text-3xl" data-testid="edge-score-value">{score?.toFixed(1)} / 100</div>
            <div className="text-[10px] mono uppercase text-muted-foreground mt-1">calc v{edge.calc_version}</div>
          </div>
          <div className="p-3 bg-card/40"><div className="label-cap">Confidence</div><div className="mono text-sm">{edge.confidence_tier}</div></div>
          <div className="p-3 bg-card/40"><div className="label-cap">Weight Coverage</div><div className="mono text-sm">{edge.weight_coverage_pct?.toFixed(0)}%</div></div>
          <div className="p-3 bg-card/40"><div className="label-cap">Strongest</div><div className="mono text-[11px]">{COMPONENT_LABELS[edge.strongest_component] || "—"}</div></div>
          <div className="p-3 bg-card/40"><div className="label-cap">Weakest</div><div className="mono text-[11px]">{COMPONENT_LABELS[edge.weakest_component] || "—"}</div></div>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground" data-testid="edge-ineligible">
          The AI Edge Score is intentionally hidden until eligibility thresholds are met.
          This is by design — a small sample of trades can produce misleading scores.
        </div>
      )}
      {edge?.recommended_next_action && (
        <div className="text-[11px] text-[#0A84FF] flex items-start gap-2 border-t border-border/40 pt-2">
          <Info size={12} className="mt-0.5 shrink-0"/>
          <span>Recommended next action: {edge.recommended_next_action}</span>
        </div>
      )}
    </div>
  );
}

function EligibilityChecklist({ edge }) {
  if (!edge?.eligibility?.checks) return null;
  return (
    <div className="grid-cell !p-3 space-y-2" data-testid="edge-eligibility">
      <div className="mono uppercase text-[10px] tracking-widest text-muted-foreground">Eligibility Checklist</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {edge.eligibility.checks.map((c) => (
          <div key={c.gate} className="flex items-center gap-2 text-xs"
               data-testid={`edge-check-${c.gate}`}>
            {c.pass ? <CheckCircle2 size={13} className="text-signal-green shrink-0"/> :
                        <XCircle size={13} className="text-signal-red shrink-0"/>}
            <span className="mono">{c.gate.replace(/_/g, " ")}</span>
            <span className="ml-auto mono text-[10px] text-muted-foreground">
              required {typeof c.required === "boolean" ? "yes" : c.required} · current {fmt(c.current)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ComponentBreakdown({ edge }) {
  if (!edge?.components) return null;
  const entries = Object.entries(edge.components);
  return (
    <div className="grid-cell !p-0 overflow-auto" data-testid="edge-components">
      <table className="w-full text-xs">
        <thead className="bg-card/60 border-b border-border/60 text-[10px] uppercase mono text-muted-foreground tracking-widest">
          <tr>
            <th className="p-2 text-left">Component</th>
            <th className="p-2 text-right">Weight</th>
            <th className="p-2 text-right">Sub-Score</th>
            <th className="p-2 text-left">Contribution</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([k, v]) => {
            const s = v.score;
            const missing = s == null;
            const barTone = missing ? "bg-border/40" : s >= 60 ? "bg-signal-green" : s >= 40 ? "bg-signal-yellow" : "bg-signal-red";
            return (
              <tr key={k} className="border-b border-border/40" data-testid={`edge-comp-${k}`}>
                <td className="p-2 mono">{COMPONENT_LABELS[k] || k}</td>
                <td className="p-2 mono text-right">{(v.weight * 100).toFixed(1)}%</td>
                <td className={`p-2 mono text-right ${missing ? "text-muted-foreground italic" : ""}`}>
                  {missing ? "Unavailable" : s.toFixed(1)}
                </td>
                <td className="p-2 min-w-[120px]">
                  <div className="h-1.5 bg-secondary/40 relative overflow-hidden">
                    <div className={`absolute inset-y-0 left-0 ${barTone}`} style={{ width: `${missing ? 0 : s}%` }}/>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ReadinessCard({ readiness, onRecompute, recomputing }) {
  if (!readiness) return null;
  const status = readiness.status;
  const tone = READINESS_TONE[status] || READINESS_TONE["Not Ready"];
  return (
    <div className="grid-cell !p-4 space-y-3" data-testid="readiness-card">
      <div className="flex items-center gap-3 flex-wrap">
        <ShieldAlert size={16} className="text-signal-yellow"/>
        <div className="mono uppercase text-[10px] tracking-widest text-muted-foreground">Live Trading Readiness</div>
        <div className={`chip !text-[10px] uppercase ${tone}`} data-testid="readiness-status">{status}</div>
        <div className="chip !text-[10px] uppercase border-signal-red text-signal-red flex items-center gap-1" data-testid="live-locked">
          <Lock size={10}/> Live Trading OFF
        </div>
        <button className="btn-outline ml-auto" onClick={onRecompute} disabled={recomputing} data-testid="readiness-recompute">
          {recomputing ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>}
          Recompute
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border/60">
        <div className="p-3 bg-card/40"><div className="label-cap">Passed Gates</div><div className="mono text-lg text-signal-green">{readiness.passed_gates}/{readiness.total_gates}</div></div>
        <div className="p-3 bg-card/40"><div className="label-cap">Failed Gates</div><div className="mono text-lg text-signal-red">{readiness.failed_gates}</div></div>
        <div className="p-3 bg-card/40"><div className="label-cap">Missing Data</div><div className="mono text-lg text-muted-foreground">{readiness.missing_gates}</div></div>
        <div className="p-3 bg-card/40"><div className="label-cap">Edge Score</div><div className="mono text-lg">{readiness.edge_score?.toFixed(1) ?? "—"}</div></div>
      </div>
      {readiness.blockers?.length > 0 && (
        <div className="space-y-1" data-testid="readiness-blockers">
          <div className="mono uppercase text-[10px] tracking-widest text-signal-red">Blocking Issues</div>
          <ul className="text-[11px] text-signal-red space-y-0.5">
            {readiness.blockers.map((b, i) => <li key={i} className="flex items-start gap-1"><Lock size={10} className="mt-0.5 shrink-0"/>{b}</li>)}
          </ul>
        </div>
      )}
      {readiness.recommended_next_action && (
        <div className="text-[11px] text-[#0A84FF] flex items-start gap-2 border-t border-border/40 pt-2">
          <Info size={12} className="mt-0.5 shrink-0"/>
          <span>{readiness.recommended_next_action}</span>
        </div>
      )}
    </div>
  );
}

function GatesTable({ readiness }) {
  if (!readiness?.gates) return null;
  return (
    <div className="grid-cell !p-0 overflow-auto" data-testid="readiness-gates">
      <table className="w-full text-xs">
        <thead className="bg-card/60 border-b border-border/60 text-[10px] uppercase mono text-muted-foreground tracking-widest">
          <tr>
            <th className="p-2 text-left">Gate</th>
            <th className="p-2 text-left">Required</th>
            <th className="p-2 text-left">Current</th>
            <th className="p-2 text-center">Result</th>
          </tr>
        </thead>
        <tbody>
          {readiness.gates.map((g) => (
            <tr key={g.gate} className="border-b border-border/40" data-testid={`readiness-gate-${g.gate}`}>
              <td className="p-2 mono">{GATE_LABELS[g.gate] || g.gate}</td>
              <td className="p-2 mono text-muted-foreground">{String(g.required)}</td>
              <td className="p-2 mono">{fmt(g.current)}</td>
              <td className="p-2 text-center">
                {g.pass ? <CheckCircle2 size={14} className="text-signal-green inline"/> :
                            g.current == null ? <HelpCircle size={14} className="text-muted-foreground inline"/> :
                                                  <XCircle size={14} className="text-signal-red inline"/>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function EdgeSection() {
  const [recomputing, setRecomputing] = useState(false);

  const { data: edge, refetch: refetchEdge } = useQuery({
    queryKey: ["edge", "score"],
    queryFn: async () => (await api.post("/edge/score", {})).data,
  });
  const { data: readiness, refetch: refetchReadiness } = useQuery({
    queryKey: ["edge", "readiness"],
    queryFn: async () => (await api.post("/edge/readiness", {})).data,
  });

  const recompute = useMutation({
    mutationFn: async () => Promise.all([
      api.post("/edge/score", {}).then((r) => r.data),
      api.post("/edge/readiness", {}).then((r) => r.data),
    ]),
    onSuccess: () => { refetchEdge(); refetchReadiness(); toast.success("Edge & readiness updated"); },
    onError: () => toast.error("Recompute failed"),
    onSettled: () => setRecomputing(false),
  });

  function onRecompute() {
    setRecomputing(true);
    recompute.mutate();
  }

  if (!edge) return <div className="text-xs text-muted-foreground"><Loader2 className="inline animate-spin mr-2" size={12}/>Loading edge & readiness…</div>;

  return (
    <div className="space-y-4" data-testid="edge-section">
      <ScoreCard edge={edge} onRecompute={onRecompute} recomputing={recomputing}/>
      <EligibilityChecklist edge={edge}/>
      <ComponentBreakdown edge={edge}/>
      <ReadinessCard readiness={readiness} onRecompute={onRecompute} recomputing={recomputing}/>
      <GatesTable readiness={readiness}/>
    </div>
  );
}
